// Rep-side per-line product editor on an estimate / quote — the UI half
// of the CEO gap report (2026-07-23): "I should be able to edit the products on
// an estimate or a quote — upload new images, add logo decoration, etc."
//
// As of the unified-editor work (2026-07-24, CEO-approved part 2) these editors
// no longer render as an always-expanded panel under each line card — they are
// the Images and Imprint tab BODIES of OrderLineEditModal (see
// pages/OrderLineEditModal.tsx, now one page). The exports below are the two bodies:
//
//   • OrderLineImageEditor — replace / remove / reorder the product image on
//     THIS line's product_snapshot (snapshot-scoped; never the catalog product).
//   • OrderLineDecorationEditor — imprint locations, the 9 carried artwork
//     columns (decorator_type / imprint_type / proof_required / logo_name /
//     logo_size / logo_color / repeat_logo / supplier_notes / logo_file_id +
//     logo upload), and their setup/run charges.
//
// REUSE-VS-FORK (documented for the reviewer): the ORDER charges now share the
// cost / sell / computed-margin SHAPE with the presentation-side
// DecorationSection (PresentationDecorationSection.tsx) — CEO 2026-07-24: reps
// set the charge cost and sell, and see the margin they're charging. The
// ENDPOINTS + TABLES remain separate: order charges live on
// line_item_decoration_charges via /orders/:id/lines/:lineId/decorations* and
// /decoration_charges/* (with `amount` still the totals driver, mirrored from
// sell_price server-side); presentation charges live on
// presentation_item_decoration_charges. Parameterizing one component across
// both endpoint families would need conditional field sets + two endpoint
// families — more surface than a purpose-built order editor. So the CHARGES
// stay a deliberate FORK that REUSES the shape-compatible primitives
// (extractError, NumberField, ChargeType/CHARGE_TYPE_LABELS) and now the same
// cost/sell/margin field pattern. Margin here is READ-ONLY computed
// ((sell − cost) / sell); the rep edits cost + sell freely.
//
// MONEY: never a totals authority here. Charge cost + sell_price + billing_model
// are written via the API; totals recompute from the shared integer-cents module
// on refetch. `cost` is rep-facing DISPLAY-ONLY — never a client-facing field
// (the server strips it from /o/:uuid) and never a commission input.
import { chargeQuantity, colorCountWords, readReorder, reorderValue } from "@merchd/shared";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Star, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ArtworkFilePicker } from "@/components/artwork/ArtworkFilePicker";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  CHARGE_TYPE_LABELS,
  extractError,
  type ChargeType,
} from "@/pages/PresentationDecorationSection";
import {
  formatCents,
  isPriceableMargin,
  lineChargesCents,
  marginHundredths,
  sellPriceFromCostMills,
  toMills,
} from "@/components/documents/money";
import { PRICING_COLS } from "@/components/documents/ItemPricingTable";

type BillingModel = "fixed" | "per_piece";

/** proof_type vocabulary (SO→PO rebuild Round 2, map §Stage 4). Value ⇄ label;
 *  the values match the server enum (services/artwork-fields.ts PROOF_TYPE_VALUES
 *  + schema proof_type). Kept in this order so the select reads none → strongest. */
// Stephanie 2026-10-14: three choices, worded the way the team says them.
// digital_photo is no longer offered; a decoration that already has it still
// shows it (see the select below) so nothing silently changes.
const PROOF_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "none", label: "No proof required" },
  { value: "email_proof", label: "E-mail digital proof" },
  { value: "pre_production", label: "E-mail pre-production photo" },
];

interface EditorCharge {
  id: string;
  chargeType: string;
  amount: string;
  /** Rep-facing cost / sell / generated margin (CEO 2026-07-24). Optional on the
   *  type because leaner/client-facing charge payloads (DocCharge) omit them;
   *  GET /orders/:id always returns them for the rep editor. */
  cost?: string;
  sellPrice?: string;
  marginPct?: string | null;
  billingModel: BillingModel;
  note: string | null;
  /** false = "Subtract from margin" (we cover it). Absent before Jake's DDL. */
  displayToClient?: boolean;
  /** The rep's count; null/absent = the default (fixed 1, run = the units). */
  quantity?: number | null;
}

/** Is the charge Qty box typeable yet (Jake's push-charge-quantity step)? The
 *  quote page provides it from GET /orders/:id (Stephanie 2026-10-27). */
export const ChargeQtyReadyContext = createContext(false);

/** Read-only margin display for a cost/sell pair — mirrors the DB generated
 *  column ((sell − cost) / sell). "—" when sell is 0/blank/non-numeric. */
function marginDisplay(cost: string, sell: string): string {
  const s = Number(sell);
  const c = Number(cost);
  if (!Number.isFinite(s) || s === 0 || !Number.isFinite(c)) return "—";
  return `${(((s - c) / s) * 100).toFixed(1)}%`;
}

/** Order-line decoration, incl. the 9 presentation→order carry columns
 *  (round 34) the Imprint tab now exposes. All open-text except logoFileId. */
export interface EditorDecoration {
  id: string;
  /** 'item_costs' = the line's "+ Add cost" holder, not an imprint location. */
  kind?: "location" | "item_costs";
  /** Who decorates: the supplier, or a 3rd-party decorator (own PO). */
  decoratorType?: "supplier" | "third_party";
  location: string | null;
  colorCount: number | null;
  imprintTypeId?: string | null;
  imprintType?: string | null;
  proofRequired?: string | null;
  logoName?: string | null;
  logoSize?: string | null;
  logoColor?: string | null;
  repeatLogo?: string | null;
  supplierNotes?: string | null;
  logoFileId?: string | null;
  /** Artwork-as-first-class-PO-input (SO→PO rebuild Round 2). */
  proofType?: "none" | "email_proof" | "digital_photo" | "pre_production" | null;
  logoWidthIn?: string | null;
  logoHeightIn?: string | null;
  useMaxSize?: boolean;
  charges: EditorCharge[];
}

interface EditorLine {
  id: string;
  quantityTotal: number;
  productSnapshot: {
    imageUrls?: string[] | Record<string, string>;
  } | null;
  decorations: EditorDecoration[];
}

/** Array-or-object snapshot imageUrls → a plain string[] (matches the reader in
 *  components/documents/types.ts docLineImageUrl + the API normalizer). */
function snapshotImageList(line: EditorLine): string[] {
  const urls = line.productSnapshot?.imageUrls;
  if (Array.isArray(urls)) {
    return urls.filter((u): u is string => typeof u === "string" && u !== "");
  }
  if (urls && typeof urls === "object") {
    return Object.values(urls).filter(
      (u): u is string => typeof u === "string" && u !== "",
    );
  }
  return [];
}

// ─── Image editor (Images tab body) ────────────────────────────────────

export function OrderLineImageEditor(props: {
  orderId: string;
  line: EditorLine;
  detailQueryKey: readonly unknown[];
}) {
  const { orderId, line, detailQueryKey } = props;
  const queryClient = useQueryClient();
  const images = useMemo(() => snapshotImageList(line), [line]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const save = useMutation({
    mutationFn: async (nextUrls: string[]) =>
      api(`/orders/${orderId}/lines/${line.id}/snapshot-images`, {
        method: "PATCH",
        body: JSON.stringify({ image_urls: nextUrls }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  const onPickFile = async (file: File): Promise<void> => {
    setError(null);
    setUploading(true);
    try {
      const presign = await api<{
        upload_url: string;
        live: boolean;
        public_url: string | null;
      }>(`/files/presign`, {
        method: "POST",
        body: JSON.stringify({
          filename: file.name,
          mime_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          file_purpose: "presentation_image",
          visibility: "client_visible",
          prefix: `orders/${orderId}/line-images`,
        }),
      });
      if (presign.live) {
        const put = await fetch(presign.upload_url, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!put.ok) {
          throw new Error(`Image upload failed: ${put.status} ${put.statusText}`);
        }
      }
      if (!presign.public_url) {
        // R2 public URL not configured (dev/preview) — nothing safe to store.
        throw new Error(
          "Uploads aren't configured for a public URL yet, so the image can't be attached.",
        );
      }
      // New image becomes the PRIMARY (first). Existing urls are preserved.
      save.mutate([presign.public_url, ...images]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Image upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (url: string): void => {
    save.mutate(images.filter((u) => u !== url));
  };
  const makePrimary = (url: string): void => {
    save.mutate([url, ...images.filter((u) => u !== url)]);
  };

  const busy = uploading || save.isPending;

  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-brown-300">
          Product images
        </p>
        <span className="text-xs text-brown-300">
          The first one shows on the document.
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onPickFile(f);
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        {images.map((url, i) => (
          <div
            key={url}
            className={cn(
              "group relative h-40 w-40 overflow-hidden rounded-md border bg-white",
              i === 0 ? "border-peach-400" : "border-brown-100",
            )}
          >
            <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
            {i === 0 && (
              <span className="absolute left-0 top-0 bg-peach-400 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                Primary
              </span>
            )}
            <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-brown-500/70 py-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              {i !== 0 && (
                <button
                  type="button"
                  onClick={() => makePrimary(url)}
                  disabled={busy}
                  className="text-cream-50 hover:text-peach-300 disabled:opacity-50"
                  aria-label="Make primary image"
                  title="Make primary"
                >
                  <Star className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => removeAt(url)}
                disabled={busy}
                className="text-cream-50 hover:text-rust-300 disabled:opacity-50"
                aria-label="Remove image"
                title="Remove image"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            setError(null);
            inputRef.current?.click();
          }}
          disabled={busy}
          className="flex h-40 w-40 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-peach-300 bg-cream-50/40 text-xs font-semibold text-brown-400 hover:border-peach-400 hover:bg-cream-50 disabled:opacity-50"
        >
          <ImagePlus className="h-4 w-4" />
          {busy ? "Working…" : "Upload"}
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-rust-600">{error}</p>}
    </div>
  );
}

// ─── Decoration editor (the item window's Decoration section) ──────────
//
// 2026-10-14 (Stephanie, commonsku ideas B–E, Jake approved):
//   • E — a Supplier decorator / 3rd party decorator switch for the item. It
//     writes each location's existing decorator_type, the field the PO
//     builder already reads (poCarriesDecoration) to decide whose PO carries
//     the decoration charges.
//   • B — each location is ONE row: its name, one art tile, and the proof.
//     The art tile opens the Artwork window (detail fields + big preview).
//   • C — charges sit under their location as pricing rows: what it is, qty,
//     cost, margin, sell, total. "+ Run charge" (per piece), "+ Fixed charge"
//     (flat) and "+ Copy location" sit under each location.
//   • D — the Artwork window's file picker is one big picture grid.
// Every field saves on its own when you click away.

type DecoratorType = "supplier" | "third_party";

export function OrderLineDecorationEditor(props: {
  orderId: string;
  /** The owning project — powers the ArtworkFilePicker's Project Files tab. */
  projectId: string;
  line: EditorLine;
  detailQueryKey: readonly unknown[];
  /** Told when the Artwork window opens/closes, so the item window behind it
   *  leaves Escape and its backdrop alone meanwhile. */
  onArtworkWindow?: (open: boolean) => void;
  /** Jake's DDL has run: offer Display to client / Subtract from margin. */
  itemCostsReady?: boolean;
}) {
  const { orderId, projectId, line, detailQueryKey, onArtworkWindow, itemCostsReady } = props;
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [artworkFor, setArtworkForState] = useState<string | null>(null);
  const setArtworkFor = (id: string | null): void => {
    setArtworkForState(id);
    onArtworkWindow?.(id !== null);
  };

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: detailQueryKey });
  };

  // Imprint locations only — the "+ Add cost" holder has its own section.
  const decorations = useMemo(
    () =>
      // In the order they were added (art first, Stephanie 2026-10-14).
      line.decorations.filter((d) => d.kind !== "item_costs"),
    [line.decorations],
  );

  // The item's decorator: what every location says. With no locations yet the
  // rep's pick is held here and given to the first location they add.
  const stored = new Set(decorations.map((d) => d.decoratorType ?? "supplier"));
  const [pendingType, setPendingType] = useState<DecoratorType>("supplier");
  const decoratorType: DecoratorType | null =
    decorations.length === 0
      ? pendingType
      : stored.size === 1
        ? ([...stored][0] as DecoratorType)
        : null; // mixed — neither side lit until the rep picks one

  const setDecorator = useMutation({
    mutationFn: async (next: DecoratorType) => {
      for (const d of decorations) {
        if ((d.decoratorType ?? "supplier") === next) continue;
        await api(`/orders/${orderId}/lines/${line.id}/decorations/${d.id}`, {
          method: "PATCH",
          body: JSON.stringify({ decorator_type: next }),
        });
      }
    },
    onError: (err) => setError(extractError(err)),
    onSettled: invalidate,
  });

  // Art first (Stephanie 2026-10-14): a location is created only once its art
  // is uploaded or picked — then its name, proof and charges appear under it.
  const [pickingArt, setPickingArtState] = useState(false);
  const setPickingArt = (open: boolean): void => {
    setPickingArtState(open);
    onArtworkWindow?.(open);
  };
  const addDecoration = useMutation({
    mutationFn: async (logoFileId: string) =>
      api(`/orders/${orderId}/lines/${line.id}/decorations`, {
        method: "POST",
        // The logo name starts as the art's file name (Stephanie 2026-10-26).
        body: JSON.stringify({ logo_file_id: logoFileId, decorator_type: decoratorType ?? "supplier", logo_name: await artFileName(logoFileId) }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      setPickingArt(false);
      invalidate();
    },
  });

  const artworkDecoration = decorations.find((d) => d.id === artworkFor) ?? null;

  return (
    <div>
      <div className="mb-4 space-y-2">
        <DecoratorSwitch
          value={decoratorType}
          pending={setDecorator.isPending}
          onChange={(next) => {
            if (decorations.length === 0) setPendingType(next);
            else setDecorator.mutate(next);
          }}
        />
        <p className="text-sm text-brown-300">
          {decoratorType === "third_party"
            ? "We send blanks to a decorator — the decoration charges go on the decorator's PO."
            : decoratorType === "supplier"
              ? "The supplier decorates — the decoration charges go on the supplier's PO."
              : "Locations disagree — pick one to set them all."}
        </p>
      </div>

      {error && <p className="mb-2 text-sm text-rust-600">{error}</p>}

      {decorations.length > 0 && (
        <div className="divide-y divide-brown-50 rounded-lg border border-brown-100 bg-white">
          {decorations.map((d, i) => (
            <OrderDecorationRow
              key={d.id}
              index={i}
              orderId={orderId}
              lineId={line.id}
              lineQuantity={line.quantityTotal}
              decoration={d}
              onOpenArtwork={() => setArtworkFor(d.id)}
              onMutated={invalidate}
              displayChoice={itemCostsReady === true}
            />
          ))}
        </div>
      )}

      {/* Always one empty upload box at the end — no location exists until
          its art is picked (Stephanie 2026-10-14: "just another box to
          upload"). */}
      <button
        type="button"
        onClick={() => setPickingArt(true)}
        className={cn(
          "flex h-44 w-full max-w-md flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-peach-300 bg-cream-50/40 text-brown-400 hover:border-peach-400 hover:bg-cream-50",
          decorations.length > 0 && "mt-4",
        )}
      >
        <ImagePlus className="h-7 w-7" />
        <span className="text-[16px] font-semibold">
          {decorations.length === 0 ? "Upload or pick the art" : "Upload or pick more art"}
        </span>
        <span className="text-sm text-brown-300">Its location and charges come next.</span>
      </button>

      {pickingArt && (
        <AddArtWindow
          projectId={projectId}
          pending={addDecoration.isPending}
          onPick={(fileId) => addDecoration.mutate(fileId)}
          onClose={() => setPickingArt(false)}
        />
      )}

      {artworkDecoration && (
        <ArtworkWindow
          orderId={orderId}
          projectId={projectId}
          lineId={line.id}
          decoration={artworkDecoration}
          onMutated={invalidate}
          onClose={() => setArtworkFor(null)}
        />
      )}
    </div>
  );
}

/** Supplier decorator / 3rd party decorator — the commonsku pill switch. */
function DecoratorSwitch(props: {
  value: DecoratorType | null;
  pending: boolean;
  onChange: (v: DecoratorType) => void;
}) {
  const { value, pending, onChange } = props;
  const opt = (v: DecoratorType, label: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={value === v}
      disabled={pending}
      onClick={() => value !== v && onChange(v)}
      className={cn(
        "rounded-full px-4 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60",
        value === v ? "bg-ink text-white" : "text-brown-400 hover:text-brown-500",
      )}
    >
      {label}
    </button>
  );
  return (
    <div role="radiogroup" aria-label="Who decorates" className="inline-flex rounded-full bg-cream-100 p-1">
      {opt("supplier", "Supplier decorator")}
      {opt("third_party", "3rd party decorator")}
    </div>
  );
}

/** One PATCH path for a decoration's fields, shared by the row and the
 *  Artwork window. */
function useDecorationPatch(args: {
  orderId: string;
  lineId: string;
  decorationId: string;
  onMutated: () => void;
  onError: (msg: string | null) => void;
}) {
  const { orderId, lineId, decorationId, onMutated, onError } = args;
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api(`/orders/${orderId}/lines/${lineId}/decorations/${decorationId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onError: (err) => onError(extractError(err)),
    onSuccess: () => {
      onError(null);
      onMutated();
    },
  });
}

function OrderDecorationRow(props: {
  index: number;
  orderId: string;
  lineId: string;
  lineQuantity: number;
  decoration: EditorDecoration;
  onOpenArtwork: () => void;
  onMutated: () => void;
  displayChoice?: boolean;
}) {
  const { index, orderId, lineId, lineQuantity, decoration: d, onOpenArtwork, onMutated, displayChoice } =
    props;
  const [locationDraft, setLocationDraft] = useState(d.location ?? "");
  useEffect(() => setLocationDraft(d.location ?? ""), [d.location]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [copyingToItems, setCopyingToItems] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch = useDecorationPatch({
    orderId,
    lineId,
    decorationId: d.id,
    onMutated,
    onError: setError,
  });

  const del = useMutation({
    mutationFn: async () =>
      api(`/orders/${orderId}/lines/${lineId}/decorations/${d.id}`, {
        method: "DELETE",
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  // Copy location: a new location with the same artwork, details and
  // charges, so a second spot is a tweak rather than a re-type.
  const copy = useMutation({
    mutationFn: async () => {
      const res = await api<{ decoration: { id: string } }>(
        `/orders/${orderId}/lines/${lineId}/decorations`,
        {
          method: "POST",
          body: JSON.stringify(decorationCopyBody(d, `${d.location?.trim() || "Location"} (copy)`)),
        },
      );
      for (const c of d.charges) {
        await api(`/decorations/${res.decoration.id}/charges`, {
          method: "POST",
          body: JSON.stringify({
            charge_type: c.chargeType,
            cost: c.cost ?? "0",
            sell_price: c.sellPrice ?? c.amount,
            billing_model: c.billingModel,
            note: c.note,
            ...(c.quantity != null ? { quantity: c.quantity } : {}),
          }),
        }).then(async (r) => {
          const created = r as { charge?: { id: string } };
          if (c.displayToClient === false && created.charge) {
            await api(`/decoration_charges/${created.charge.id}`, {
              method: "PATCH",
              body: JSON.stringify({ display_to_client: false }),
            });
          }
        });
      }
    },
    onError: (err) => setError(extractError(err)),
    onSettled: onMutated,
  });

  const [adding, setAdding] = useState<BillingModel | null>(null);

  const saveLocation = (): void => {
    const next = locationDraft.trim();
    if (next === "" ) {
      setLocationDraft(d.location ?? "");
      return;
    }
    if (next !== (d.location ?? "").trim()) patch.mutate({ location: next });
  };

  // Art first (Stephanie 2026-10-14): the art on top; once there is art, the
  // location bar (name + proof) under it, then the charges. A location that
  // already had a name or charges before art keeps showing them.
  const showFields =
    d.logoFileId != null || (d.location ?? "").trim() !== "" || d.charges.length > 0;
  return (
    <div className="space-y-4 p-4">
      {/* The art; click it for its details (like commonsku's Update Artwork). */}
      <div>
        <ArtTile fileId={d.logoFileId ?? null} onOpen={onOpenArtwork} />
        {showFields && <ArtworkSummary decoration={d} onOpen={onOpenArtwork} />}
      </div>

      {showFields && (
      <>
      {/* Location bar: where it goes + the proof, side by side. */}
      <div className="flex flex-wrap items-start gap-4">
        <label className="block w-full max-w-md">
          <span className={FIELD_LABEL}>Location {index + 1}</span>
          <Input
            type="text"
            value={locationDraft}
            onChange={(e) => setLocationDraft(e.target.value)}
            onBlur={saveLocation}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            maxLength={200}
            placeholder="Where does it go? e.g. Left Chest"
            className={cn("h-10 px-3 text-[15px]", locationDraft.trim() === "" && "border-rust-300")}
          />
        </label>
      </div>
      {error && <p className="text-sm text-rust-600">{error}</p>}

      <OrderChargeRows
        decorationId={d.id}
        charges={d.charges}
        lineQuantity={lineQuantity}
        adding={adding}
        onDoneAdding={() => setAdding(null)}
        onMutated={onMutated}
        displayChoice={displayChoice}
      />

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[15px] font-semibold text-rust-500">
        <button type="button" onClick={() => setAdding("per_piece")} className="hover:text-rust-600">
          + Run charge
        </button>
        <button type="button" onClick={() => setAdding("fixed")} className="hover:text-rust-600">
          + Fixed charge
        </button>
        <button
          type="button"
          onClick={() => copy.mutate()}
          disabled={copy.isPending}
          className="hover:text-rust-600 disabled:opacity-50"
        >
          {copy.isPending ? "Copying…" : "+ Copy location"}
        </button>
        <button type="button" onClick={() => setCopyingToItems((v) => !v)} className="hover:text-rust-600">
          + Copy to other items
        </button>
        {!confirmingDelete ? (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            className="inline-flex items-center gap-1.5 text-brown-300 hover:text-rust-500"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        ) : (
          <span className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={() => del.mutate()}
              disabled={del.isPending}
              className="rounded-md bg-ink px-3 py-1.5 text-sm font-semibold text-white hover:bg-ink-700 disabled:opacity-50"
            >
              {del.isPending ? "Deleting…" : "Yes, delete it"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              disabled={del.isPending}
              className="text-sm font-semibold text-brown-300 hover:text-brown-500"
            >
              Keep
            </button>
          </span>
        )}
      </div>
      {copyingToItems && (
        <CopyToItemsPanel
          orderId={orderId}
          fromLineId={lineId}
          decoration={d}
          onClose={() => setCopyingToItems(false)}
          onCopied={onMutated}
        />
      )}
      </>
      )}
      {!showFields && (
        <button
          type="button"
          onClick={() => del.mutate()}
          disabled={del.isPending}
          className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-brown-300 hover:text-rust-500"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </button>
      )}
    </div>
  );
}

/** The fields that make up a location's artwork + details — what Copy
 *  location and Copy to other items carry over. */
function decorationCopyBody(d: EditorDecoration, location: string) {
  return {
    location,
    decorator_type: d.decoratorType ?? "supplier",
    imprint_type_id: d.imprintTypeId ?? null,
    imprint_type: d.imprintType ?? null,
    color_count: d.colorCount,
    proof_type: d.proofType ?? null,
    logo_name: d.logoName ?? null,
    logo_color: d.logoColor ?? null,
    repeat_logo: d.repeatLogo ?? null,
    supplier_notes: d.supplierNotes ?? null,
    logo_file_id: d.logoFileId ?? null,
    logo_width_in: d.useMaxSize ? null : (d.logoWidthIn ?? null),
    logo_height_in: d.useMaxSize ? null : (d.logoHeightIn ?? null),
    use_max_size: d.useMaxSize ?? false,
  };
}

/** Copy this location's artwork and details onto other items on the quote
 *  (Stephanie 2026-10-27: "if we are using the same logo on multiple items,
 *  i dont want to have to enter it for each item"). Charges are NOT copied —
 *  each item's vendor prices its own. */
function CopyToItemsPanel(props: {
  orderId: string;
  fromLineId: string;
  decoration: EditorDecoration;
  onClose: () => void;
  onCopied: () => void;
}) {
  const { orderId, fromLineId, decoration: d } = props;
  const linesQ = useQuery({
    queryKey: ["order", orderId, "copy-art-targets"],
    queryFn: () =>
      api<{ lines: Array<{ id: string; serviceType: string; description: string | null; productSnapshot: { name?: string } | null }> }>(
        `/orders/${orderId}`,
      ),
  });
  const targets = (linesQ.data?.lines ?? []).filter((l) => l.serviceType === "product" && l.id !== fromLineId);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<string | null>(null);
  const copy = useMutation({
    mutationFn: async () => {
      for (const id of picked) {
        await api(`/orders/${orderId}/lines/${id}/decorations`, {
          method: "POST",
          body: JSON.stringify(decorationCopyBody(d, d.location?.trim() || "Location")),
        });
      }
      return picked.size;
    },
    onSuccess: (n) => {
      setDone(`Copied to ${n} ${n === 1 ? "item" : "items"}. Add each item's own charges there.`);
      setPicked(new Set());
      props.onCopied();
    },
  });
  return (
    <div className="mt-2 rounded-lg border border-brown-200 bg-cream-50 p-4 text-sm font-normal text-brown-500">
      <p className="font-semibold">Copy this artwork and its details to:</p>
      {linesQ.isLoading ? (
        <p className="mt-2 text-brown-300">Loading items…</p>
      ) : targets.length === 0 ? (
        <p className="mt-2 text-brown-300">There are no other items on this quote.</p>
      ) : (
        <div className="mt-2 space-y-1.5">
          {targets.map((l) => (
            <label key={l.id} className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={picked.has(l.id)}
                onChange={(e) =>
                  setPicked((s) => {
                    const n = new Set(s);
                    if (e.target.checked) n.add(l.id);
                    else n.delete(l.id);
                    return n;
                  })
                }
                className="h-4 w-4 rounded border-brown-200"
              />
              {l.productSnapshot?.name || l.description || "Item"}
            </label>
          ))}
        </div>
      )}
      {copy.isError && <p className="mt-2 text-rust-600">{extractError(copy.error)}</p>}
      {done && <p className="mt-2 text-ok">{done}</p>}
      <div className="mt-3 flex items-center gap-3">
        <Button type="button" size="sm" disabled={picked.size === 0 || copy.isPending} onClick={() => copy.mutate()}>
          {copy.isPending ? "Copying…" : picked.size > 1 ? `Copy to ${picked.size} items` : "Copy"}
        </Button>
        <button type="button" onClick={props.onClose} className="text-sm font-semibold text-brown-300 hover:text-brown-500">
          Close
        </button>
      </div>
      <p className="mt-2 text-xs text-brown-300">Charges aren&apos;t copied — each item&apos;s vendor prices its own.</p>
    </div>
  );
}

interface FileMetaLite {
  original_filename: string;
  mime_type: string;
  is_image: boolean;
  view_url: string | null;
  /** A picture of PDF / AI / EPS art (Stephanie 2026-10-26: "the artwork
   *  should show a preview"). */
  preview_url?: string | null;
}

/** What to show for a file: itself if it's a picture, else its preview. */
const pictureOf = (m: FileMetaLite | undefined): string | null =>
  m ? (m.is_image && m.view_url ? m.view_url : m.preview_url ?? null) : null;

/** What the art's details say, in one line under the art — click it (or
 *  the art) for the details window. */
function ArtworkSummary(props: { decoration: EditorDecoration; onOpen: () => void }) {
  const d = props.decoration;
  const size = d.useMaxSize
    ? "max size"
    : d.logoWidthIn && d.logoHeightIn
      ? `${Number(d.logoWidthIn)}″ × ${Number(d.logoHeightIn)}″`
      : null;
  const proof = PROOF_TYPE_OPTIONS.find((o) => o.value === d.proofType)?.label ?? null;
  const bits = [d.imprintType?.trim() || null, colorCountWords(d.colorCount), d.logoName?.trim() || null, size, proof].filter((b): b is string => b !== null);
  return (
    <button type="button" onClick={props.onOpen} className="mt-2 block text-left text-sm text-brown-400 hover:text-brown-500">
      {bits.length > 0 ? bits.join(" · ") : "Click the art to add its details"}
    </button>
  );
}

/** The art file's own name ("WRTSBocaRaton.ai"), for a blank logo name. */
async function artFileName(fileId: string): Promise<string | null> {
  try {
    const m = await api<FileMetaLite>(`/files/${fileId}`);
    return m.original_filename?.trim().slice(0, 200) || null;
  } catch {
    return null;
  }
}

function useFileMeta(fileId: string | null) {
  return useQuery({
    queryKey: ["file-meta", fileId],
    queryFn: () => api<FileMetaLite>(`/files/${fileId}`),
    enabled: fileId !== null,
    staleTime: 5 * 60 * 1000,
  });
}

/** The one art tile on a location row — click to open the Artwork window. */
function ArtTile(props: { fileId: string | null; onOpen: () => void }) {
  const { fileId, onOpen } = props;
  const { data } = useFileMeta(fileId);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={fileId ? "Open artwork" : "Add artwork"}
      className={cn(
        "flex h-40 w-64 items-center justify-center overflow-hidden rounded-md border bg-cream-50/60",
        fileId
          ? "border-brown-100 hover:border-peach-400"
          : "border-dashed border-peach-300 hover:border-peach-400 hover:bg-cream-50",
      )}
    >
      {fileId && pictureOf(data) ? (
        <img src={pictureOf(data)!} alt={data!.original_filename} className="h-full w-full object-contain p-1" />
      ) : fileId ? (
        <span className="px-2 text-center text-sm font-semibold text-brown-400">
          {data?.original_filename ?? "Artwork on file"}
        </span>
      ) : (
        <span className="flex flex-col items-center gap-1 text-sm font-semibold text-brown-400">
          <ImagePlus className="h-5 w-5" />
          Add artwork
        </span>
      )}
    </button>
  );
}

// ─── Add art (art first) ─────────────────────────────────────────────────
// The big picture picker on its own: upload new art or pick a project file.
// Picking one creates the location (with that art) — nothing is made before.

function AddArtWindow(props: {
  projectId: string;
  pending: boolean;
  onPick: (fileId: string) => void;
  onClose: () => void;
}) {
  const { projectId, pending, onPick, onClose } = props;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-brown-500/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add art"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-[1000px] flex-col rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-brown-50 px-6 py-4">
          <h2 className="font-display text-xl font-semibold text-brown-500">Add art</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-brown-300 hover:bg-cream-50 hover:text-brown-500"
            aria-label="Close add art"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <ArtworkFilePicker
            projectId={projectId}
            logoFileId={null}
            onSelect={(fileId) => onPick(fileId)}
            onClear={() => undefined}
            large
          />
          {pending && <p className="mt-3 text-sm text-brown-300">Adding…</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Artwork window (opened from a location's art tile) ────────────────

function ArtworkWindow(props: {
  orderId: string;
  projectId: string;
  lineId: string;
  decoration: EditorDecoration;
  onMutated: () => void;
  onClose: () => void;
}) {
  const { orderId, projectId, lineId, decoration: d, onMutated, onClose } = props;
  const [error, setError] = useState<string | null>(null);
  const patch = useDecorationPatch({
    orderId,
    lineId,
    decorationId: d.id,
    onMutated,
    onError: setError,
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: async () => api(`/orders/${orderId}/lines/${lineId}/decorations/${d.id}`, { method: "DELETE" }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      onMutated();
      onClose();
    },
  });
  const { data: meta } = useFileMeta(d.logoFileId ?? null);

  // Escape closes THIS window only. Capture phase + stopImmediatePropagation
  // keeps the item window's own Escape handler from closing it too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-brown-500/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Artwork — ${d.location ?? "location"}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-[1200px] flex-col rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-brown-50 px-6 py-4">
          <h2 className="font-display text-xl font-semibold text-brown-500">
            Update artwork — {d.location?.trim() || "Untitled location"}
          </h2>
          <div className="flex items-center gap-2">
            {/* Like commonsku: Delete here; no Save — everything saves on its own. */}
            {!confirmDelete ? (
              <button type="button" onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-1.5 rounded-md border border-rust-300 px-3 py-1.5 text-sm font-semibold text-rust-500 hover:bg-rust-50">
                <Trash2 className="h-4 w-4" /> Delete location
              </button>
            ) : (
              <span className="inline-flex items-center gap-2 text-sm">
                <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className="rounded-md bg-ink px-3 py-1.5 font-semibold text-white disabled:opacity-50">
                  {remove.isPending ? "Deleting…" : "Yes, delete it"}
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="font-semibold text-brown-300 hover:text-brown-500">Keep</button>
              </span>
            )}
            <Button type="button" size="sm" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>

        <div className="grid flex-1 gap-8 overflow-y-auto px-6 py-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* Left: the details, one list. */}
          <div className="space-y-3">
            <ArtworkDetailFields decoration={d} patch={patch} orderId={orderId} lineId={lineId} />
            {error && <p className="text-sm text-rust-600">{error}</p>}
          </div>
          {/* Right: the art, and where to change it. */}
          <div className="min-w-0">
            <div className="flex h-[340px] items-center justify-center overflow-hidden rounded-lg border border-brown-100 bg-cream-50/60">
              {d.logoFileId && pictureOf(meta) ? (
                <img src={pictureOf(meta)!} alt={meta!.original_filename} className="h-full w-full object-contain p-3" />
              ) : d.logoFileId ? (
                <span className="text-[15px] font-semibold text-brown-400">
                  {meta?.original_filename ?? "Artwork on file"} — no preview for this file type
                </span>
              ) : (
                <span className="text-[15px] text-brown-300">No artwork yet — pick or upload one below.</span>
              )}
            </div>
            <ArtworkFilePicker
              projectId={projectId}
              logoFileId={d.logoFileId ?? null}
              onSelect={(fileId) => {
                // New art and no logo name yet → the file's name (Stephanie 2026-10-26).
                void (async () => {
                  const name = d.logoName?.trim() ? null : await artFileName(fileId);
                  patch.mutate({ logo_file_id: fileId, ...(name ? { logo_name: name } : {}) });
                })();
              }}
              onClear={() => patch.mutate({ logo_file_id: null })}
              large
            />
          </div>

        </div>

        <div className="border-t border-brown-50 bg-cream-50/40 px-6 py-2.5 text-sm text-brown-300" aria-live="polite">
          {patch.isPending ? "Saving…" : "Everything saves on its own. Proof, artwork and size are needed before a PO can go out."}
        </div>
      </div>
    </div>
  );
}

/** The art's details, like commonsku's "Update Artwork": one list, label on
 *  the left (Stephanie 2026-10-26: "lets make this flow better like
 *  commonsku"). Each field saves on its own — no Save button. */
function ArtworkDetailFields(props: { decoration: EditorDecoration; patch: ReturnType<typeof useDecorationPatch>; orderId: string; lineId: string }) {
  const { decoration: d, patch, orderId, lineId } = props;
  const SELECT = "h-10 w-full rounded-md border border-brown-100 bg-white px-2 text-[15px] text-brown-500 focus:border-peach-400 focus:outline-none";
  return (
    // Every field's own label sits in a left column, its box on the right.
    <div className="space-y-3 [&>*]:grid [&>*]:grid-cols-[150px_minmax(0,1fr)] [&>*]:items-start [&>*]:gap-x-4 [&>*>*:first-child]:pt-2 [&>*>*:not(:first-child)]:col-start-2">
      <ImprintTypeSelect
        imprintTypeId={d.imprintTypeId ?? null}
        typed={d.imprintType ?? null}
        onPick={(t) =>
          patch.mutate(
            t ? { imprint_type_id: t.id, imprint_type: t.displayName } : { imprint_type_id: null, imprint_type: null },
          )
        }
      />
      {/* No Colors field here — Stephanie removed it 2026-10-26. */}
      <label>
        <span className={FIELD_LABEL}>Proof required</span>
        <select
          value={d.proofType ?? ""}
          onChange={(e) => patch.mutate({ proof_type: e.target.value === "" ? null : e.target.value })}
          aria-label="Proof required"
          className={SELECT}
        >
          <option value="">Not set</option>
          {d.proofType === "digital_photo" && <option value="digital_photo">Digital photo (old choice)</option>}
          {PROOF_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {d.proofType == null && (d.proofRequired ?? "").trim() !== "" && (
          <p className="mt-1 text-xs italic text-brown-300">
            Old note: &ldquo;{d.proofRequired}&rdquo; — pick a proof above to replace it.
          </p>
        )}
      </label>
      <OrderArtworkField
        label="Logo name"
        value={d.logoName ?? null}
        onSave={(v) => patch.mutate({ logo_name: v })}
        placeholder="e.g. Acme primary mark"
      />
      <OrderLogoSizeFields
        widthIn={d.logoWidthIn ?? null}
        heightIn={d.logoHeightIn ?? null}
        useMaxSize={d.useMaxSize ?? false}
        legacyLogoSize={d.logoSize ?? null}
        onPatch={(body) => patch.mutate(body)}
      />
      <OrderArtworkField
        label="Logo color"
        value={d.logoColor ?? null}
        onSave={(v) => patch.mutate({ logo_color: v })}
        placeholder="e.g. PMS 2935 C, White"
      />
      <ReorderField value={d.repeatLogo ?? null} orderId={orderId} lineId={lineId} onSave={(v) => patch.mutate({ repeat_logo: v })} />
      <OrderArtworkField
        label="Supplier notes"
        value={d.supplierNotes ?? null}
        onSave={(v) => patch.mutate({ supplier_notes: v })}
        placeholder="Anything the decorator needs — bleed, placement, re-order PO #…"
        multiline
      />
    </div>
  );
}

/** "Re-Order?" (was "Repeat logo?" — Stephanie 2026-10-26). Yes opens a box
 *  for the previous PO #: type it, or pick one of this client's past POs for
 *  this item. Saves on its own ("Yes — PO #33252"). */
function ReorderField(props: { value: string | null; orderId: string; lineId: string; onSave: (v: string | null) => void }) {
  const { value, orderId, lineId, onSave } = props;
  const read = readReorder(value);
  const [answer, setAnswer] = useState(read.answer);
  const [po, setPo] = useState(read.po);
  useEffect(() => {
    const r = readReorder(value);
    setAnswer(r.answer);
    setPo(r.po);
  }, [value]);
  const past = useQuery({
    queryKey: ["line-past-pos", orderId, lineId],
    queryFn: () => api<{ pos: Array<{ poId: string; poNumber: number; createdAt: string; projectName: string; vendorName: string }> }>(`/orders/${orderId}/lines/${lineId}/past-pos`),
    enabled: answer === "Yes",
    staleTime: 60_000,
  });
  const save = (a: typeof answer, p: string): void => {
    const next = reorderValue(a, p);
    if ((next ?? "") !== (value ?? "").trim()) onSave(next);
  };
  return (
    <div>
      <span className={FIELD_LABEL}>Re-Order?</span>
      <div className="space-y-2">
        <select
          value={answer}
          onChange={(e) => {
            const a = e.target.value as typeof answer;
            setAnswer(a);
            save(a, a === "Yes" ? po : "");
          }}
          aria-label="Re-Order?"
          className="h-10 w-full rounded-md border border-brown-100 bg-white px-2 text-[15px] text-brown-500 focus:border-peach-400 focus:outline-none"
        >
          <option value="">—</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        {answer === "Yes" && (
          <div className="rounded-md border border-peach-300 bg-peach-50/40 p-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-brown-500">Previous PO #</span>
              <Input
                type="text"
                value={po}
                onChange={(e) => setPo(e.target.value)}
                onBlur={() => save("Yes", po)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                maxLength={40}
                placeholder="e.g. 33252"
                className="h-9 px-3 text-[15px]"
              />
            </label>
            <p className="mt-2 text-xs font-semibold text-brown-400">Or pick a past order of this item for this client</p>
            {past.isLoading ? (
              <p className="mt-1 text-xs text-brown-300">Looking…</p>
            ) : (past.data?.pos ?? []).length === 0 ? (
              <p className="mt-1 text-xs text-brown-300">No past POs of this item for this client in Flow.</p>
            ) : (
              <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto">
                {past.data!.pos.map((x) => (
                  <li key={x.poId}>
                    <button
                      type="button"
                      onClick={() => {
                        setPo(String(x.poNumber));
                        save("Yes", String(x.poNumber));
                      }}
                      className={cn(
                        "w-full rounded border px-2 py-1 text-left text-sm",
                        po === String(x.poNumber) ? "border-ink bg-white font-semibold text-ink" : "border-brown-100 bg-white text-brown-500 hover:border-peach-400",
                      )}
                    >
                      PO #{x.poNumber} · {x.projectName}
                      <span className="block text-xs text-brown-300">{x.vendorName} · {new Date(x.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const FIELD_LABEL = "mb-1 block text-sm font-semibold text-brown-500";

// ─── Artwork field (inline editable open-text) ─────────────────────────
// Blank clears the column (writes null).

function OrderArtworkField(props: {
  label: string;
  value: string | null;
  onSave: (v: string | null) => void;
  placeholder: string;
  multiline?: boolean;
}) {
  // Always typeable; saves on its own when you click away (Stephanie
  // 2026-10-14: "not click edit and save"). Blank clears the column (null).
  const { label, value, onSave, placeholder, multiline } = props;
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  const commit = (): void => {
    const next = draft.trim();
    if (next === (value ?? "").trim()) return;
    onSave(next === "" ? null : next);
  };
  return (
    <label className="block">
      <span className={FIELD_LABEL}>{label}</span>
      {multiline ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          rows={3}
          maxLength={4000}
          className="w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-[15px] text-brown-500 placeholder:italic placeholder:text-brown-300 focus:border-peach-400 focus:outline-none"
          placeholder={placeholder}
        />
      ) : (
        <Input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          maxLength={200}
          placeholder={placeholder}
          className="h-10 px-3 text-[15px] placeholder:italic"
        />
      )}
    </label>
  );
}

// ─── Imprint size — W × H + "Set to Maximum Size" (SO→PO R2) ─────────────
// Logo W/H are imprint DIMENSIONS in inches (numeric(6,4) → string), NOT money.
// "Set to Maximum Size" turns OFF manual entry: checking it clears W/H in the
// same PATCH (review A7) so no stale dimension survives implying a computed max
// that doesn't exist (Q1: there's no queryable per-location max-area yet — the
// box just records the intent to use it). Client-side bounds mirror the server's
// logoDimensionSchema (services/artwork-fields.ts) so we reject the same values.
// legacyLogoSize renders the deprecated free-text logo_size read-only (A3) —
// the column is kept, just no longer editable.

function OrderLogoSizeFields(props: {
  widthIn: string | null;
  heightIn: string | null;
  useMaxSize: boolean;
  legacyLogoSize: string | null;
  onPatch: (body: Record<string, unknown>) => void;
}) {
  const { widthIn, heightIn, useMaxSize, legacyLogoSize, onPatch } = props;
  const [wDraft, setWDraft] = useState(widthIn ?? "");
  const [hDraft, setHDraft] = useState(heightIn ?? "");
  const [dimError, setDimError] = useState<string | null>(null);

  const saveDim = (
    field: "logo_width_in" | "logo_height_in",
    raw: string,
    current: string | null,
  ): void => {
    const v = raw.trim();
    if (v === "") {
      setDimError(null);
      if (current != null) onPatch({ [field]: null });
      return;
    }
    if (!/^\d+(\.\d{1,4})?$/.test(v)) {
      setDimError("Use a number with up to 4 decimals.");
      return;
    }
    const n = Number(v);
    if (!(n > 0 && n <= 99.9999)) {
      setDimError("Each side must be greater than 0 and at most 99.9999 inches.");
      return;
    }
    setDimError(null);
    if (current == null || Number(current) !== n) onPatch({ [field]: v });
  };

  const onToggleMaxSize = (checked: boolean): void => {
    setDimError(null);
    if (checked) {
      // Clear both the drafts and the stored dims in ONE call (A7): the server
      // refine also rejects use_max_size=true alongside non-null W/H.
      setWDraft("");
      setHDraft("");
      onPatch({ use_max_size: true, logo_width_in: null, logo_height_in: null });
    } else {
      onPatch({ use_max_size: false });
    }
  };

  return (
    <div>
      <span className={FIELD_LABEL}>Imprint size (inches)</span>
      {legacyLogoSize && legacyLogoSize.trim() !== "" && (
        <p className="mb-1 text-xs italic text-brown-300">
          Old note: &ldquo;{legacyLogoSize}&rdquo; — re-enter as width × height below.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-sm font-semibold text-brown-400">
          W
          <Input
            type="text"
            inputMode="decimal"
            value={useMaxSize ? "" : wDraft}
            onChange={(e) => setWDraft(e.target.value)}
            onBlur={() => saveDim("logo_width_in", wDraft, widthIn)}
            disabled={useMaxSize}
            placeholder="—"
            className="h-10 w-20 px-2 text-center text-[15px] disabled:opacity-50"
          />
        </label>
        <span className="text-sm text-brown-300" aria-hidden>
          ×
        </span>
        <label className="flex items-center gap-1.5 text-sm font-semibold text-brown-400">
          H
          <Input
            type="text"
            inputMode="decimal"
            value={useMaxSize ? "" : hDraft}
            onChange={(e) => setHDraft(e.target.value)}
            onBlur={() => saveDim("logo_height_in", hDraft, heightIn)}
            disabled={useMaxSize}
            placeholder="—"
            className="h-10 w-20 px-2 text-center text-[15px] disabled:opacity-50"
          />
        </label>
        <label
          className="ml-2 flex cursor-pointer items-center gap-2 text-sm text-brown-400"
          title="Turns off manual width/height and records that this logo should print at the location's maximum imprint area."
        >
          <input
            type="checkbox"
            checked={useMaxSize}
            onChange={(e) => onToggleMaxSize(e.target.checked)}
            className="h-4 w-4 rounded border-peach-300 text-rust-500 focus:ring-peach-200"
          />
          Set to Maximum Size
        </label>
      </div>
      {dimError && <p className="mt-1 text-sm text-rust-600">{dimError}</p>}
    </div>
  );
}

// ─── "+ Add cost" — the item's extra costs (Stephanie 2026-10-14) ───────
// "Anything we don't have as a setup option": rows in the item's Pricing
// table, like commonsku — free-text name with the Display to client /
// Subtract from margin choice, Qty, Cost, Margin, Sell, Total. Stored as
// charges on the line's one "Additional costs" holder (made on the first add).

export function OrderItemCostRows(props: {
  orderId: string;
  line: EditorLine;
  detailQueryKey: readonly unknown[];
  /** The draft being added from the "+ Add cost" links under the table. */
  adding: BillingModel | null;
  onDoneAdding: () => void;
}) {
  const { orderId, line, detailQueryKey, adding, onDoneAdding } = props;
  const queryClient = useQueryClient();
  const holder = line.decorations.find((d) => d.kind === "item_costs") ?? null;
  const madeHolder = useRef<string | null>(null);
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: detailQueryKey });
  };
  const home = async (): Promise<string> => {
    if (holder) return holder.id;
    if (madeHolder.current) return madeHolder.current;
    const res = await api<{ decoration: { id: string } }>(
      `/orders/${orderId}/lines/${line.id}/item-costs`,
      { method: "POST", body: "{}" },
    );
    madeHolder.current = res.decoration.id;
    return res.decoration.id;
  };
  return (
    <OrderChargeRows
      decorationId={holder ? holder.id : home}
      charges={holder?.charges ?? []}
      lineQuantity={line.quantityTotal}
      adding={adding}
      onDoneAdding={onDoneAdding}
      onMutated={invalidate}
      itemCost
      displayChoice
      inPricingTable
    />
  );
}

// ─── Charges — pricing-style rows under each location (idea C) ─────────
// What it is · Qty · Cost · Margin · Sell · Total. Cost and Sell save on blur
// (PATCH /decoration_charges/:id; the server mirrors amount from sell_price).
// Margin is typeable too (Stephanie 2026-10-14: "sometimes we enter what margin
// we want to see what price it gives us"): it sets Sell = Cost / (1 − margin)
// via the shared sellPriceFromCostMills, and saves that sell. Otherwise it
// shows the margin of the cost and sell entered. The row total comes from
// the shared money module (lineChargesCents) — never a local multiplication.
// A new row is a draft until it has a sell price; then it is created.

// Qty is just a number — a narrow column, so the charge label has room
// (Stephanie 2026-10-27: "the quantity box is too large and cuts off the text").
const CHARGE_COLS =
  "grid grid-cols-[28px_minmax(0,1fr)_56px_110px_90px_110px_110px] items-center gap-3";
/** With the Display to client / Subtract from margin column (once Jake's DDL
 *  has run): Charge · Qty · Cost · Margin · Sell · Client · Total. */
const CHARGE_COLS_WITH_DISPLAY =
  "grid grid-cols-[28px_minmax(0,1fr)_56px_110px_90px_110px_190px_110px] items-center gap-3";

/** Where a new charge row is created: a decoration id, or — for "+ Add cost" —
 *  a function that finds or makes the item's cost holder first. */
type ChargeHome = string | (() => Promise<string>);

function OrderChargeRows(props: {
  decorationId: ChargeHome;
  charges: EditorCharge[];
  lineQuantity: number;
  /** A draft row being added: per_piece = "+ Run charge", fixed = "+ Fixed charge". */
  adding: BillingModel | null;
  onDoneAdding: () => void;
  onMutated: () => void;
  /** "+ Add cost" rows: a free-text name, no charge type (Stephanie 2026-10-14:
   *  "anything we don't have as a setup option"). */
  itemCost?: boolean;
  /** Show the Display to client / Subtract from margin choice per row. */
  displayChoice?: boolean;
  /** Rows inside the item's Pricing table (commonsku): its grid, no header. */
  inPricingTable?: boolean;
}) {
  const { decorationId, charges, lineQuantity, adding, onDoneAdding, onMutated, itemCost, displayChoice, inPricingTable } = props;
  const cols = displayChoice ? CHARGE_COLS_WITH_DISPLAY : CHARGE_COLS;
  const sorted = useMemo(
    () => [...charges].sort((a, b) => a.chargeType.localeCompare(b.chargeType)),
    [charges],
  );
  if (sorted.length === 0 && adding === null) return null;
  if (inPricingTable) {
    return (
      <>
        {sorted.map((c) => (
          <ChargeRow
            key={c.id}
            charge={c}
            decorationId={decorationId}
            lineQuantity={lineQuantity}
            onMutated={onMutated}
            itemCost={itemCost}
            displayChoice={displayChoice}
            inPricingTable
          />
        ))}
        {adding !== null && (
          <ChargeRow
            key={`draft-${adding}`}
            charge={null}
            draftBilling={adding}
            decorationId={decorationId}
            lineQuantity={lineQuantity}
            onMutated={() => {
              onDoneAdding();
              onMutated();
            }}
            onCancelDraft={onDoneAdding}
            itemCost={itemCost}
            displayChoice={displayChoice}
            inPricingTable
          />
        )}
      </>
    );
  }
  return (
    <div className="mt-4 overflow-x-auto">
      <div className={displayChoice ? "min-w-[980px]" : "min-w-[780px]"}>
        <div className={cn(cols, "pb-1 text-xs font-bold uppercase tracking-wide text-brown-300")}>
          <span />
          <span>{itemCost ? "Cost" : "Charge"}</span>
          <span>Qty</span>
          <span className="text-right">Cost</span>
          <span className="text-right">Margin</span>
          <span className="text-right">Sell</span>
          {displayChoice && <span>Client</span>}
          <span className="text-right">Total</span>
        </div>
        {sorted.map((c) => (
          <ChargeRow
            key={c.id}
            charge={c}
            decorationId={decorationId}
            lineQuantity={lineQuantity}
            onMutated={onMutated}
            itemCost={itemCost}
            displayChoice={displayChoice}
          />
        ))}
        {adding !== null && (
          <ChargeRow
            key={`draft-${adding}`}
            charge={null}
            draftBilling={adding}
            decorationId={decorationId}
            lineQuantity={lineQuantity}
            onMutated={() => {
              onDoneAdding();
              onMutated();
            }}
            onCancelDraft={onDoneAdding}
            itemCost={itemCost}
            displayChoice={displayChoice}
          />
        )}
      </div>
    </div>
  );
}

function ChargeRow(props: {
  /** null = a draft row not saved yet. */
  charge: EditorCharge | null;
  draftBilling?: BillingModel;
  decorationId: ChargeHome;
  lineQuantity: number;
  onMutated: () => void;
  onCancelDraft?: () => void;
  itemCost?: boolean;
  displayChoice?: boolean;
  inPricingTable?: boolean;
}) {
  const { charge, draftBilling, decorationId, lineQuantity, onMutated, onCancelDraft, itemCost, displayChoice, inPricingTable } = props;
  const isDraft = charge === null;
  const initialType: ChargeType = isDraft
    ? itemCost
      ? "other"
      : draftBilling === "per_piece"
        ? "run"
        : "setup"
    : (charge.chargeType as ChargeType);
  // Display to client (true) / Subtract from margin (false) — we cover it:
  // the client never sees it, its sell is held at 0, its cost lowers margin.
  const [shown, setShown] = useState(charge?.displayToClient !== false);
  // Qty (Stephanie 2026-10-27): setup defaults to 1, run to the item's units,
  // both typeable. null = follow the default.
  const qtyReady = useContext(ChargeQtyReadyContext);
  const [qty, setQty] = useState<number | null>(charge?.quantity ?? null);
  const [qtyDraft, setQtyDraft] = useState<string | null>(null);
  const [chargeType, setChargeType] = useState<ChargeType>(initialType);
  // The label fills in from the type ("Setup" → "Setup Charge") unless the
  // rep typed their own (Stephanie 2026-10-27).
  const defaultNote = (t: ChargeType): string => `${CHARGE_TYPE_LABELS[t]} Charge`;
  const [note, setNote] = useState(charge ? (charge.note ?? "") : itemCost ? "" : defaultNote(initialType));
  // sellPrice is authoritative; fall back to amount for pre-migration rows.
  const savedCost = charge?.cost ?? "0";
  const savedSell = charge ? (charge.sellPrice ?? charge.amount) : "";
  const [cost, setCost] = useState(isDraft ? "" : savedCost);
  const [sell, setSell] = useState(savedSell);
  const [billingModel, setBillingModel] = useState<BillingModel>(
    charge?.billingModel ?? draftBilling ?? "fixed",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!charge) return;
    setChargeType(charge.chargeType as ChargeType);
    setNote(charge.note ?? "");
    setCost(charge.cost ?? "0");
    setSell(charge.sellPrice ?? charge.amount);
    setBillingModel(charge.billingModel);
    setShown(charge.displayToClient !== false);
    setQty(charge.quantity ?? null);
  }, [charge]);

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (!isDraft) {
        return api(`/decoration_charges/${charge.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      }
      const home = typeof decorationId === "string" ? decorationId : await decorationId();
      const { display_to_client: show, ...fields } = body;
      const created = await api<{ charge: { id: string } }>(`/decorations/${home}/charges`, {
        method: "POST",
        body: JSON.stringify(fields),
      });
      // A new row the rep already marked "Subtract from margin".
      if (show === false) {
        await api(`/decoration_charges/${created.charge.id}`, {
          method: "PATCH",
          body: JSON.stringify({ display_to_client: false }),
        });
      }
      return created;
    },
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  const remove = useMutation({
    mutationFn: async () =>
      charge ? api(`/decoration_charges/${charge.id}`, { method: "DELETE" }) : null,
    onError: (err) => setError(extractError(err)),
    onSuccess: () => onMutated(),
  });

  const validMoney = (s: string) => /^\d+(\.\d{1,2})?$/.test(s);
  const validCost = (c: string) => /^\d+(\.\d{1,3})?$/.test(c);

  /** Save whatever changed. A draft is created once it has a valid sell. */
  const commit = (
    over: Partial<{ chargeType: ChargeType; billingModel: BillingModel; sell: string; shown: boolean; note: string; quantity: number | null }> = {},
  ): void => {
    const q = over.quantity !== undefined ? over.quantity : qty;
    const t = over.chargeType ?? chargeType;
    const b = over.billingModel ?? billingModel;
    const isShown = over.shown ?? shown;
    // Subtracted: the client pays nothing — the sell is always 0.
    const s = isShown ? (over.sell ?? sell).trim() : "0";
    const c = cost.trim() === "" ? "0" : cost.trim();
    if (s !== "" && !validMoney(s)) {
      setError("Sell must be a number, e.g. 45.00.");
      return;
    }
    if (!validCost(c)) {
      setError("Cost must be a number, e.g. 12.00 or 1.692.");
      return;
    }
    setError(null);
    const n = over.note ?? note;
    const label = n.trim() === "" ? null : n.trim();
    if (isDraft) {
      if (s === "") return; // nothing to bill yet — stays a draft
      // A covered cost needs a cost to be worth a row.
      if (!isShown && toMills(c) <= 0) return;
      save.mutate({
        charge_type: t,
        cost: c,
        sell_price: s,
        billing_model: b,
        note: label,
        ...(q != null && qtyReady ? { quantity: q } : {}),
        ...(isShown ? {} : { display_to_client: false }),
      });
      return;
    }
    const body: Record<string, unknown> = {};
    if (t !== charge.chargeType) body.charge_type = t;
    if (s === "") {
      setSell(savedSell);
    } else if (s !== savedSell) body.sell_price = s;
    if (c !== savedCost) body.cost = c;
    if (b !== charge.billingModel) body.billing_model = b;
    if (label !== (charge.note ?? null)) body.note = label;
    if (qtyReady && q !== (charge.quantity ?? null)) body.quantity = q;
    if (isShown !== (charge.displayToClient !== false)) {
      body.display_to_client = isShown;
      if (!isShown) delete body.sell_price; // the server holds it at 0
    }
    if (Object.keys(body).length > 0) save.mutate(body);
  };

  const changeShown = (next: boolean): void => {
    setShown(next);
    if (!next) setSell("0.00");
    commit({ shown: next, ...(next ? {} : { sell: "0" }) });
  };

  // Margin box: null while not being typed in, so it shows the live margin.
  const [marginDraft, setMarginDraft] = useState<string | null>(null);
  const shownMargin =
    charge?.marginPct != null && cost === savedCost && sell === savedSell
      ? `${Number(charge.marginPct).toFixed(1)}%`
      : marginDisplay(cost.trim() === "" ? "0" : cost, sell);
  /** Typed margin → the sell price it gives on this cost, then save it. */
  const applyMargin = (raw: string): void => {
    setMarginDraft(null);
    const t = raw.trim().replace(/%$/, "").trim();
    if (t === "" || `${t}%` === shownMargin) return;
    const marginH = /^-?\d+(\.\d{1,2})?$/.test(t) ? marginHundredths(t) : null;
    if (!isPriceableMargin(marginH)) {
      setError("Margin must be a number under 100, e.g. 40.");
      return;
    }
    const c = cost.trim() === "" ? "0" : cost.trim();
    if (!validCost(c) || toMills(c) <= 0) {
      setError("Enter the cost first — the margin works out the sell price from it.");
      return;
    }
    // The shared one-rounding rule the item's own margin boxes use.
    const next = sellPriceFromCostMills(toMills(c), marginH);
    if (next == null) return;
    setSell(next);
    commit({ sell: next });
  };

  const sellForTotal = validMoney(sell.trim()) ? sell.trim() : "0";
  const totalCents = lineChargesCents({
    unitPrice: "0",
    quantityTotal: lineQuantity,
    decorationCharges: [{ amount: sellForTotal, billingModel, quantity: qty }],
  });
  const defaultQty = billingModel === "per_piece" ? lineQuantity : 1;
  const shownQty = chargeQuantity({ billingModel, quantity: qty }, lineQuantity);
  const commitQty = (raw: string): void => {
    setQtyDraft(null);
    const n = Number(raw.trim());
    if (raw.trim() === "" || !Number.isInteger(n) || n < 0) return; // back to what it was
    const next = n === defaultQty ? null : n;
    if (next === qty) return;
    setQty(next);
    commit({ quantity: next });
  };

  const cell = "h-10 px-2 text-[15px]";
  const displaySelect = (
    <select
      value={shown ? "show" : "subtract"}
      onChange={(e) => changeShown(e.target.value === "show")}
      aria-label="Display to client or subtract from margin"
      className={cn(
        "h-10 rounded-md border border-brown-100 bg-white px-2 text-[15px] text-brown-500 focus:border-peach-400 focus:outline-none",
        inPricingTable ? "w-52 flex-none" : "w-full",
      )}
    >
      <option value="show">Display to client</option>
      <option value="subtract">Subtract from margin</option>
    </select>
  );
  return (
    <div className={inPricingTable ? "border-b border-brown-50 px-1 py-2" : "border-t border-brown-50 py-2"}>
      <div className={inPricingTable ? PRICING_COLS : displayChoice ? CHARGE_COLS_WITH_DISPLAY : CHARGE_COLS}>
        <button
          type="button"
          onClick={() => (isDraft ? onCancelDraft?.() : remove.mutate())}
          disabled={remove.isPending}
          className="rounded-md p-1 text-brown-300 hover:bg-rust-50 hover:text-rust-500"
          aria-label="Remove charge"
          title="Remove charge"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 gap-2">
          {!itemCost && (
          <select
            value={chargeType}
            onChange={(e) => {
              const t = e.target.value as ChargeType;
              setChargeType(t);
              // Swap the label only if it's empty or still the old default.
              const keepTyped = note.trim() !== "" && note.trim() !== defaultNote(chargeType);
              const nextNote = keepTyped ? note : defaultNote(t);
              setNote(nextNote);
              commit({ chargeType: t, note: nextNote });
            }}
            aria-label="Charge type"
            className="h-10 w-32 flex-none rounded-md border border-brown-100 bg-white px-2 text-[15px] text-brown-500 focus:border-peach-400 focus:outline-none"
          >
            {CHARGE_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {CHARGE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          )}
          <Input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => commit()}
            maxLength={200}
            autoFocus={isDraft && itemCost}
            placeholder={itemCost ? "What is it? e.g. Freight in, Bagging, Labels" : `${CHARGE_TYPE_LABELS[chargeType]} charge`}
            aria-label="Charge label"
            className={cn(cell, "min-w-0 flex-1")}
          />
          {inPricingTable && displayChoice && displaySelect}
        </div>
        {/* Just the number, like commonsku (Stephanie 2026-10-14: "no drop
            down no extra words"). "+ Run charge" bills per piece (the item's
            units), "+ Fixed charge" bills once (1). */}
        {qtyReady ? (
          <Input
            type="text"
            inputMode="numeric"
            value={qtyDraft ?? String(shownQty)}
            onFocus={(e) => {
              setQtyDraft(String(shownQty));
              e.currentTarget.select();
            }}
            onChange={(e) => setQtyDraft(e.target.value)}
            onBlur={(e) => commitQty(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") {
                e.stopPropagation();
                setQtyDraft(null);
              }
            }}
            title={billingModel === "per_piece" ? `Defaults to the item's ${lineQuantity.toLocaleString()} units` : "Defaults to 1"}
            aria-label="Charge quantity"
            className={cn(cell, "text-right")}
          />
        ) : (
          <span className={cn("text-[15px] tabular-nums text-brown-500", inPricingTable && "text-right")} aria-label="Charge quantity">
            {shownQty.toLocaleString()}
          </span>
        )}
        <Input
          type="text"
          inputMode="decimal"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          onBlur={() => commit()}
          placeholder="0.00"
          aria-label="Charge cost"
          className={cn(cell, "text-right")}
        />
        <Input
          type="text"
          inputMode="decimal"
          value={shown ? (marginDraft ?? shownMargin) : "—"}
          disabled={!shown}
          onFocus={(e) => {
            setMarginDraft(shownMargin === "—" ? "" : shownMargin.replace(/%$/, ""));
            e.currentTarget.select();
          }}
          onChange={(e) => setMarginDraft(e.target.value)}
          onBlur={(e) => applyMargin(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              e.stopPropagation();
              setMarginDraft(null);
            }
          }}
          aria-label="Charge margin"
          title={shown ? "Type a margin to set the sell price from the cost" : "We cover this cost — it lowers the item's margin"}
          className={cn(cell, "text-right disabled:bg-cream-50 disabled:text-brown-300")}
        />
        <Input
          type="text"
          inputMode="decimal"
          value={shown ? sell : "—"}
          disabled={!shown}
          onChange={(e) => setSell(e.target.value)}
          onBlur={() => commit()}
          placeholder="0.00"
          aria-label="Charge sell"
          autoFocus={isDraft && !itemCost}
          className={cn(cell, "text-right font-semibold disabled:bg-cream-50 disabled:text-brown-300")}
        />
        {displayChoice && !inPricingTable && displaySelect}
        <span className="text-right text-[15px] font-semibold tabular-nums text-brown-500">
          {formatCents(totalCents)}
        </span>
      </div>
      {isDraft && (
        <p className="mt-1 pl-10 text-sm text-brown-300">
          {save.isPending
            ? "Adding…"
            : shown
              ? "Saves once it has a sell price."
              : "Saves once it has a cost — we cover it, the client isn't charged."}
        </p>
      )}
      {error && <p className="mt-1 pl-10 text-sm text-rust-600">{error}</p>}
    </div>
  );
}

const CHARGE_TYPE_OPTIONS: ChargeType[] = [
  "setup",
  "run",
  "digitizing",
  "rush",
  "screen",
  "pms_match",
  "other",
];

/** Imprint type dropdown (Stephanie 2026-10-14: "a drop down that anyone can
 *  add to"). The list is the imprint_types master list the PO reads; "+ Add a
 *  new type…" saves a new one for everyone. A type typed before the dropdown
 *  existed still shows as the current choice until someone picks from the list. */
function ImprintTypeSelect(props: {
  imprintTypeId: string | null;
  typed: string | null;
  onPick: (t: { id: string; displayName: string } | null) => void;
}) {
  const { imprintTypeId, typed, onPick } = props;
  const queryClient = useQueryClient();
  const types = useQuery({
    queryKey: ["imprint-types"],
    queryFn: () => api<{ rows: Array<{ id: string; displayName: string }> }>("/imprint-types"),
    staleTime: 5 * 60_000,
  });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Rename / remove the picked type (Stephanie 2026-10-30: a typo stayed in
  // the shared list for good).
  const [renaming, setRenaming] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const rename = useMutation({
    mutationFn: (args: { id: string; name: string }) =>
      api(`/imprint-types/${encodeURIComponent(args.id)}`, { method: "PATCH", body: JSON.stringify({ name: args.name }) }),
    onSuccess: (_r, args) => {
      void queryClient.invalidateQueries({ queryKey: ["imprint-types"] });
      onPick({ id: args.id, displayName: args.name });
      setAdding(false);
      setRenaming(false);
      setName("");
      setError(null);
    },
    onError: () => setError("Couldn't rename that type. Try again."),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/imprint-types/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["imprint-types"] });
      setConfirmRemove(false);
      onPick(null);
    },
    onError: () => setError("Couldn't remove that type. Try again."),
  });
  const add = useMutation({
    mutationFn: (n: string) =>
      api<{ row: { id: string; displayName: string } }>("/imprint-types", {
        method: "POST",
        body: JSON.stringify({ name: n }),
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ["imprint-types"] });
      onPick(res.row);
      setAdding(false);
      setName("");
      setError(null);
    },
    onError: () => setError("Couldn't add that type. Try again."),
  });
  const rows = types.data?.rows ?? [];
  const legacy = !imprintTypeId && typed ? typed : null;
  const label = (
    <span className="mb-1 block text-sm font-semibold text-brown-500">Imprint type</span>
  );

  if (adding) {
    return (
      <label className="block">
        {label}
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          autoFocus
          placeholder="e.g. Silkscreen"
          aria-label="New imprint type"
          className="h-10 px-3 text-[15px]"
        />
        <div className="mt-1 flex items-center gap-2">
          <button
            type="button"
            disabled={name.trim() === "" || add.isPending || rename.isPending}
            onClick={() => (renaming && imprintTypeId ? rename.mutate({ id: imprintTypeId, name: name.trim() }) : add.mutate(name.trim()))}
            className="rounded-md bg-peach-400 px-2 py-0.5 text-sm font-semibold text-white hover:bg-peach-500 disabled:opacity-50"
          >
            {renaming ? (rename.isPending ? "Saving…" : "Save name") : add.isPending ? "Adding…" : "Add"}
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setRenaming(false);
              setError(null);
            }}
            className="text-sm font-semibold text-brown-400 hover:text-brown-600"
          >
            Cancel
          </button>
          {error && <span className="text-sm text-rust-600">{error}</span>}
        </div>
      </label>
    );
  }

  return (
    <label className="block">
      {label}
      <select
        value={imprintTypeId ?? (legacy ? "__typed" : "")}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__add") {
            setAdding(true);
            return;
          }
          if (v === "__typed") return;
          onPick(v === "" ? null : (rows.find((r) => r.id === v) ?? null));
        }}
        aria-label="Imprint type"
        className="block h-10 w-full rounded-md border border-brown-100 bg-white px-2 text-[15px] text-brown-500 focus:border-peach-400 focus:outline-none"
      >
        <option value="">{types.isLoading ? "Loading…" : "Not set"}</option>
        {legacy && <option value="__typed">{legacy}</option>}
        {rows.map((r) => (
          <option key={r.id} value={r.id}>
            {r.displayName}
          </option>
        ))}
        <option value="__add">+ Add a new type…</option>
      </select>
      {imprintTypeId && (
        <span className="mt-1 flex flex-wrap items-center gap-3 text-xs">
          {confirmRemove ? (
            <>
              <span className="text-brown-500">Take “{rows.find((r) => r.id === imprintTypeId)?.displayName}” off the list for everyone?</span>
              <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(imprintTypeId)} className="font-semibold text-rust-600 hover:underline">
                Yes, remove
              </button>
              <button type="button" onClick={() => setConfirmRemove(false)} className="text-brown-400 hover:text-brown-600">No</button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setName(rows.find((r) => r.id === imprintTypeId)?.displayName ?? "");
                  setRenaming(true);
                  setAdding(true);
                }}
                className="font-medium text-brown-400 hover:text-brown-600"
              >
                Rename type
              </button>
              <button type="button" onClick={(e) => { e.preventDefault(); setConfirmRemove(true); }} className="font-medium text-brown-400 hover:text-brown-600">
                Remove from list
              </button>
            </>
          )}
          {error && <span className="text-rust-600">{error}</span>}
        </span>
      )}
    </label>
  );
}
